import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import ComputerUseApi, { type ComputerUseRecording } from "../apis/computerUseApi";
import type { ComputerUseRunState } from "../reducers/chatReducer";

/**
 * The panel reads the store, never the API response directly.
 *
 * Both feed the same slice -- the stored rows seed it on a cold load and the live stream keeps
 * it current -- so taking the store's shape is what makes one component able to draw a run
 * whether or not this client was watching when it happened.
 */
type Run = ComputerUseRunState;

const LIVE_STATUSES = ["in_queue", "executing", "waiting_on_customer"];

/**
 * Set by the reducer, never by the server: the job is finished by the time the provider starts
 * making the video, so this is a state of the panel rather than of the job.
 */
const PREPARING_RECORDING = "preparing_recording";

// How long a recording may still be arriving before its absence is reported as a failure, and
// how often to ask. The upload follows the session's close, so the run a technician has just
// watched is the one most likely to be asked for too early -- and the one they are most likely
// to be looking at. Generous: waiting shows a spinner, giving up shows an error about a
// recording that is on its way.
const RECORDING_WAIT_MS = 90000;
const RECORDING_POLL_MS = 3000;

const BORDER = "1px solid #D8DEE8";

/**
 * Line the panel up with the message content it belongs to, rather than the thread's edge.
 *
 * ChatBubble lays out an avatar and the text beside it, so everything a technician reads --
 * the reply, the review controls, the action buttons -- starts inset by the same amount:
 * 24px of avatar, its 4px right margin, and the 6px padding inside the bubble's Paper.
 */
const CHAT_CONTENT_INDENT = "34px";

/**
 * The session's own size. No viewport is configured, so hyperbrowser's default applies --
 * 1280x720, which is what the recordings come back as.
 *
 * The iframe is held at exactly this size and scaled visually to fit, rather than being
 * resized to its container. The live view is a cross-origin page that lays itself out for
 * whatever viewport it is given and does not reflow when that viewport shrinks -- collapsing
 * the expanded view left it rendering at the large size, showing a corner of the screen. A
 * constant inner viewport removes the reflow entirely instead of trying to trigger one we
 * cannot reach.
 */
const LIVE_NATIVE_WIDTH = 1280;
const LIVE_NATIVE_HEIGHT = 720;

const LIVE_ASPECT = "16 / 9";

/**
 * Inline, the panel is as wide as the browser it shows rather than as wide as the
 * conversation: a browser is a window, and stretching it to the column made it read as a
 * page. It sits left, like everything else in the thread.
 */
const LIVE_WIDTH = 760;

/**
 * Expanded, it takes the screen -- bounded so the 16:9 box never grows taller than the
 * viewport, which capping width alone would not prevent.
 */
const LIVE_EXPANDED_WIDTH = "min(1400px, 94vw, calc(88vh * 16 / 9))";

/** Above MUI's modal layer, so nothing in the app draws over the expanded browser. */
const LIVE_EXPANDED_Z = 1300;

/**
 * Rounded and clipped, like the recording player.
 *
 * The clipping is on the wrapper rather than the iframe because an iframe does not clip its
 * own content to its border radius -- the corners would stay square.
 */
const FRAME_BASE = {
  borderRadius: 2,
  overflow: "hidden",
  border: BORDER,
  background: "#FFFFFF",
};

const inlineFrameSx = {
  ...FRAME_BASE,
  position: "relative" as const,
  width: LIVE_WIDTH,
  maxWidth: "100%",
  aspectRatio: LIVE_ASPECT,
};

const expandedFrameSx = {
  ...FRAME_BASE,
  position: "fixed" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: LIVE_EXPANDED_Z + 1,
  width: LIVE_EXPANDED_WIDTH,
  aspectRatio: LIVE_ASPECT,
  boxShadow: "0 24px 64px rgba(9, 14, 26, 0.45)",
};

/**
 * How long to keep the browser covered when the script never says it has navigated.
 *
 * A backstop, not the mechanism. Scripts announce PAGE_READY and the cover lifts on that; this
 * only catches one that does not -- an older script, or one that fails before its first page --
 * where waiting forever would hide a browser that is working perfectly well. Generous enough
 * not to pre-empt a slow first navigation, short enough not to be a wait of its own.
 */
const LIVE_REVEAL_FALLBACK_MS = 8000;

/** A finished run is a record, not the main event, so its player takes a segment of the width. */
const PLAYER_WIDTH = 360;
const PLAYER_HEIGHT = 202;

export interface ComputerUseRunProps {
  run: Run;
  aiThreadId: number | string;
  /**
   * Which of the run's browsers this panel is for.
   *
   * A recording draws that one video; `null` draws the browser that is still open. One session
   * per panel, because a run's sessions happen at different points in the conversation -- an
   * attempt that stopped at a sign-in wall, the sign-in, the retry -- and drawing them together
   * put them all at the first one's position, with the messages that explain them elsewhere.
   */
  recording?: Run["recordings"][number] | null;
}

/**
 * One browser session, rendered where it happened in the conversation.
 *
 * A session has three looks and moves through them in order: a live browser the technician can
 * watch, a short wait while the provider makes the video, then the player. The wait is real and
 * unskippable -- the recording is made provider-side and only starts existing once the session
 * has stopped -- so it is shown rather than hidden behind a spinner with no explanation.
 *
 * The three occupy the same place in the transcript, so a running browser is replaced in place
 * by its own recording rather than moving when it finishes.
 */
const ComputerUseRun: React.FC<ComputerUseRunProps> = ({ run, aiThreadId, recording = null }) => {
  // A panel drawn for a finished recording is never the live one, whatever the run is doing:
  // a rescued run can have a second browser open while this panel shows the first one's video.
  const isLive = recording === null && LIVE_STATUSES.includes(run.status);
  const [liveExpanded, setLiveExpanded] = useState(false);

  /**
   * The one state in which the browser is the technician's to touch.
   *
   * Every other moment Theo is mid-task, and a stray click lands in a real admin console --
   * so the feed is watch-only by default and opens up only when Theo has asked for a hand.
   */
  const interactive = run.status === "waiting_on_customer";

  // Escape collapses, matching the click-outside it pairs with.
  useEffect(() => {
    if (!liveExpanded) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLiveExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [liveExpanded]);

  // The browser closing while expanded would strand the overlay with nothing under it.
  useEffect(() => {
    if (!isLive) setLiveExpanded(false);
  }, [isLive]);

  /**
   * How far the fixed-size iframe has to shrink to fill its container.
   *
   * Measured rather than derived from the width constants: the inline frame is capped at 100%
   * on a narrow window and the expanded one is sized in viewport units, so neither has a width
   * known at build time.
   */
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;

    const measure = () => {
      const width = frame.clientWidth;
      if (width > 0) setScale(width / LIVE_NATIVE_WIDTH);
    };
    measure();

    // Absent in jsdom, and only an optimisation for later resizes -- the measure above is what
    // makes the first paint correct.
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [isLive, run.browserUrl, liveExpanded]);

  /**
   * Whether to show the browser or the wait in front of it.
   *
   * `browserReady` is the real answer and comes from the script itself. The timer is only for
   * a script that never sends one: a cover that never lifts would be a worse bug than the
   * black frame this replaces.
   */
  const [fallbackReveal, setFallbackReveal] = useState(false);
  const revealed = run.browserReady || fallbackReveal;

  useEffect(() => {
    if (!run.browserUrl || run.browserReady) return undefined;
    setFallbackReveal(false);
    const timer = setTimeout(() => setFallbackReveal(true), LIVE_REVEAL_FALLBACK_MS);
    return () => clearTimeout(timer);
    // Keyed on the URL so a rescue's second browser gets its own wait rather than inheriting
    // the first one's expired timer.
  }, [run.browserUrl, run.browserReady]);

  /**
   * The browser has closed but the provider is still making the video.
   *
   * A cancelled run counts, and this is not a detail: stopping a run is what closes its session,
   * and closing the session is what starts the recording being made. A stop therefore lands here
   * almost every time -- but only once a session actually opened, since a run stopped before that
   * has nothing to record and never will.
   *
   * Unless the run said it was not being recorded. Not every closed session produces a video any
   * more: a run that only walked up to a sign-in wall, and the browser the technician signs in
   * through, are both stopped without one. Waiting is the wrong default for those, and the wait
   * has no end -- the spinner sat there for a recording that was never started.
   */
  const awaitingRecording =
    recording === null &&
    !run.recordingSkipped &&
    (run.status === PREPARING_RECORDING || (run.status === "cancelled" && run.sessionId !== null));
  const preparingRecording = awaitingRecording && !run.recordingError;

  // A settled run with nothing to play means the video never arrived at all -- which is worth
  // saying only when one was expected. A session deliberately left unrecorded is not missing
  // anything, and telling the technician it is would send them looking.
  const finishedWithoutRecording = recording === null && !isLive && !preparingRecording && !run.recordingSkipped;

  return (
    // No width here: a block already fills its parent, and 100% alongside the indent would
    // push the panel past the thread's right edge.
    <Box sx={{ my: 1.5, ml: CHAT_CONTENT_INDENT }}>
      <Header run={run} isLive={isLive} />

      {isLive && run.browserUrl ? (
        <>
          {/*
            Expanding restyles the frame in place; it never moves it. Rendering the iframe
            into a Dialog would put it under a portal -- a different parent -- which remounts
            it and reloads the live session, dropping the technician back to the start. Hence
            a hand-rolled backdrop rather than the Dialog used for the finished recordings,
            and hence this one staying mounted and merely hidden.
          */}
          <Box
            data-testid="computer-use-live-backdrop"
            onClick={() => setLiveExpanded(false)}
            sx={{
              display: liveExpanded ? "block" : "none",
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(9, 14, 26, 0.62)",
              zIndex: LIVE_EXPANDED_Z,
            }}
          />

          <Box ref={frameRef} sx={liveExpanded ? expandedFrameSx : inlineFrameSx}>
            <Box
              component="iframe"
              title="Theo's browser session"
              src={run.browserUrl}
              sx={{
                display: "block",
                position: "absolute",
                top: 0,
                left: 0,
                width: LIVE_NATIVE_WIDTH,
                height: LIVE_NATIVE_HEIGHT,
                border: "none",
                transformOrigin: "top left",
                transform: `scale(${scale})`,
              }}
            />

            {/*
              Covered, not unmounted. The browser has a session but no page yet -- the script
              has still to be spawned and to navigate -- and the live view of an empty tab is
              black. The iframe loads underneath in the meantime, so lifting this reveals a
              page that is already connected rather than starting the wait over.
            */}
            {revealed ? null : (
              <Box
                data-testid="computer-use-live-cover"
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  background: "#FBFCFF",
                }}
              >
                <CircularProgress size={16} sx={{ color: "#8A93AB" }} />
                <Typography sx={{ fontSize: 12, color: "#5B6583", fontFamily: "Inter, sans-serif" }}>
                  Opening the browser...
                </Typography>
              </Box>
            )}

            {interactive ? null : (
              <Box
                aria-hidden
                title="Theo is working in this browser"
                sx={{ position: "absolute", inset: 0, cursor: "not-allowed" }}
              />
            )}

            <Tooltip title={liveExpanded ? "Shrink the browser" : "Open the browser larger"}>
              <IconButton
                onClick={() => setLiveExpanded((open) => !open)}
                aria-label={liveExpanded ? "Shrink the browser view" : "Open the browser in a larger view"}
                size="small"
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  // Above the click blocker, which would otherwise swallow this button too.
                  zIndex: 1,
                  bgcolor: "rgba(15, 23, 42, 0.6)",
                  color: "#FFFFFF",
                  "&:hover": { bgcolor: "rgba(15, 23, 42, 0.85)" },
                }}
              >
                {liveExpanded ? (
                  <CloseFullscreenIcon sx={{ fontSize: 16 }} />
                ) : (
                  <OpenInFullIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      ) : null}

      {isLive && !run.browserUrl ? <Waiting label="Starting a browser..." /> : null}

      {preparingRecording ? <Waiting label="Preparing the recording..." /> : null}

      {recording ? (
        <RecordingPlayer
          aiThreadId={aiThreadId}
          jobId={run.jobId}
          recording={recording}
          // No "Part 1 / Part 2". They existed because a row of identical thumbnails is
          // unreadable; a recording standing in its own place, after the message that led to
          // it and before the one about what it did, is explained by the transcript itself.
          label={null}
        />
      ) : null}

      {finishedWithoutRecording ? (
        <Typography sx={{ fontSize: 12, color: "#8A93AB", fontFamily: "Inter, sans-serif" }}>
          No recording is available for this run.
        </Typography>
      ) : null}
    </Box>
  );
};

const Header: React.FC<{ run: Run; isLive: boolean }> = ({ run, isLive }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
    {isLive ? (
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: run.status === "waiting_on_customer" ? "#F59E0B" : "#22C55E",
          boxShadow: "0 0 8px rgba(34, 197, 94, 0.7)",
        }}
      />
    ) : null}
    <Typography sx={{ fontSize: 12, color: "#5B6583", fontWeight: 600, fontFamily: "Inter, sans-serif" }}>
      {headerLabel(run, isLive)}
    </Typography>
  </Box>
);

function headerLabel(run: Run, isLive: boolean): string {
  // Not "waiting for you to sign in": the same panel now also waits while the technician types a
  // 1Password deletion password, which is not a sign-in. The specific ask lives on the review
  // card's heading and body; the panel banner just says a person is needed.
  if (run.status === "waiting_on_customer") return "Theo's browser is waiting for you";
  // Before the plain live label. Without it the technician watches the page they were following
  // reset itself with no explanation, which reads as a fault rather than a second go.
  if (isLive && run.retry) return `Theo is trying again (attempt ${run.retry.tryNumber} of ${run.retry.maxTries})`;
  if (isLive) return "Theo is using a browser";
  if (run.status === PREPARING_RECORDING) return "Theo has finished in the browser";
  if (run.status === "cancelled") return "Browser session cancelled";
  // Before the error case, and the order is the point. A run that walked up to a sign-in wall
  // and stopped settles as an error like any other, so it read as "Browser session failed" --
  // over a recording of Theo doing exactly the right thing, and directly above the card asking
  // for the help it had just stopped to ask for.
  if (run.needsTechnician) return "Theo stopped here and needs your help";
  // The two generic endings, and the only two the run's own title replaces. Every label above
  // says something the title cannot -- that a person is needed, that somebody pressed Stop --
  // and would be a worse header for saying less.
  //
  // The title is written by the script that did the work, so a recording found months later
  // explains itself instead of having to be watched.
  if (run.status === "error") return run.title || "Browser session failed";
  return run.title || "Browser session recording";
}

const Waiting: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.5,
      py: 2,
      border: BORDER,
      borderRadius: 2,
      background: "#FBFCFF",
    }}
  >
    <CircularProgress size={16} sx={{ color: "#8A93AB" }} />
    <Typography sx={{ fontSize: 12, color: "#5B6583", fontFamily: "Inter, sans-serif" }}>{label}</Typography>
  </Box>
);

interface RecordingPlayerProps {
  aiThreadId: number | string;
  jobId: number | null;
  recording: ComputerUseRecording;
  label: string | null;
}

/**
 * One session's recording, loading itself as soon as the thread opens.
 *
 * The link is signed and short-lived, and GCS checks it on every request rather than once at
 * the start -- so a video the browser has not finished fetching can have its next range
 * request refused mid-playback. Letting it preload in full is most of the answer: once the
 * bytes are in the buffer no further request is made and the link's age stops mattering. The
 * re-sign below covers what preloading does not, since `preload` is a hint the browser is free
 * to ignore on a large file or a metered connection.
 */
const RecordingPlayer: React.FC<RecordingPlayerProps> = ({ aiThreadId, jobId: runJobId, recording, label }) => {
  // The recording's OWN job, falling back to the run's only when it has none.
  //
  // A run is keyed by the tool call and one call can span several jobs -- a HITL pause resumed
  // under it, then the work that follows -- while the run adopts the first job it saw. Signing a
  // later job's video against that first job asked the server for a pairing that does not exist:
  // the lookup filters on job id, matched nothing, and the player sat on a 404 for a video that
  // was sitting in storage the whole time.
  const jobId = recording.job_id ?? runJobId;
  const [videoUrl, setVideoUrl] = useState<string | null>(recording.video_url);
  const [posterUrl, setPosterUrl] = useState<string | null>(recording.thumbnail_url);
  const [error, setError] = useState<string | null>(null);
  /**
   * Whether the file has told us anything about itself yet.
   *
   * `preload="auto"` fetches the whole recording, which is megabytes, so there is a real
   * stretch between the element mounting and it having a frame to show. Unmarked, that stretch
   * is a dark rectangle sitting in the transcript looking like a recording that failed.
   */
  const [loaded, setLoaded] = useState(false);
  const api = useMemo(() => new ComputerUseApi(), []);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  /** Where playback had reached when a link died, restored once its replacement arrives. */
  const resumeAt = useRef<number | null>(null);
  /** Guards the retry: one re-sign per failure, cleared as soon as playback resumes. */
  const resigning = useRef(false);

  /**
   * Fetch a link for a recording that has none.
   *
   * Two ways to arrive here. A recording that appeared on the live stream was never signed --
   * the stream names the object and cannot sign it -- and one whose link expired had its
   * cleared deliberately, just below.
   */
  useEffect(() => {
    if (videoUrl || jobId == null || !recording.session_id) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const deadline = Date.now() + RECORDING_WAIT_MS;

    const ask = () => {
      api
        .getRecordingUrl(aiThreadId, jobId, recording.session_id)
        .then(({ url }) => {
          if (!cancelled && url) setVideoUrl(url);
        })
        .catch((err: any) => {
          if (cancelled) return;
          // A 404 here usually means "not yet", not "never". The recording is banked when the
          // session stops and uploaded afterwards, while this component mounts the moment the
          // run reports finished -- so the first ask for the run a technician just watched
          // regularly lands before the file exists. Reported as a failure it read as a lost
          // recording, and reloading the page a few seconds later showed it playing.
          if (err?.response?.status === 404 && Date.now() < deadline) {
            timer = setTimeout(ask, RECORDING_POLL_MS);
            return;
          }
          setError(err?.message || "This recording could not be loaded.");
        });
    };

    ask();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [api, aiThreadId, jobId, recording.session_id, videoUrl]);

  /**
   * Re-sign, silently, and pick playback back up where it stopped.
   *
   * The viewer is not told: an expiring link is our bookkeeping, not something they did. The
   * buffer is lost with the old URL -- a re-signed link is a different URL as far as the cache
   * is concerned -- so this costs a stutter, not a restart from the beginning.
   */
  const onError = useCallback(() => {
    if (jobId == null || resigning.current) {
      setError("This recording could not be loaded.");
      return;
    }
    resigning.current = true;
    resumeAt.current = videoRef.current?.currentTime ?? null;
    setVideoUrl(null);
  }, [jobId]);

  /**
   * Fetch a poster for a recording that has none.
   *
   * The run that just finished in front of the technician is the one most likely to be looked
   * at and the only one with no poster: it arrived on the live stream, which names the objects
   * and cannot sign either. Without this it is the one recording that shows a dark rectangle
   * while it loads -- exactly the wrong way round.
   *
   * Failure is silent. A missing poster costs a plain frame for a few seconds; saying so would
   * put an error on a recording that is working.
   */
  useEffect(() => {
    if (posterUrl || jobId == null || !recording.session_id) return undefined;

    let cancelled = false;
    api
      .getPosterUrl(aiThreadId, jobId, recording.session_id)
      .then(({ url }) => {
        if (!cancelled && url) setPosterUrl(url);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, aiThreadId, jobId, recording.session_id, posterUrl]);

  // A re-signed link is a fresh element with an empty buffer, so the wait starts again.
  useEffect(() => setLoaded(false), [videoUrl]);

  const onLoadedMetadata = useCallback(() => {
    setLoaded(true);
    const video = videoRef.current;
    if (video && resumeAt.current != null) {
      video.currentTime = resumeAt.current;
      resumeAt.current = null;
      void video.play().catch(() => undefined);
    }
  }, []);

  // Cleared only once playback is genuinely running again, so a recording that is simply
  // broken cannot cycle: it fails, re-signs once, fails again, and settles on saying so.
  const onPlaying = useCallback(() => {
    resigning.current = false;
    setError(null);
  }, []);

  /*
    The poster sits on the frame, not only on the video.

    A recording that arrived on the live stream has no link yet, so there is no video element
    to hang a `poster` attribute on -- and that is precisely the recording a technician is
    watching for. Painting it here fills the space whether or not the element exists, and the
    video's own poster takes over seamlessly once it does.
  */
  const frame = {
    position: "relative" as const,
    width: PLAYER_WIDTH,
    maxWidth: "100%",
    height: PLAYER_HEIGHT,
    borderRadius: 2,
    overflow: "hidden",
    border: BORDER,
    background: "#0B1220",
    ...(posterUrl
      ? {
          backgroundImage: `url("${posterUrl}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : {}),
  };

  return (
    <Box data-testid="computer-use-recording-frame" sx={frame}>
      {videoUrl ? (
        <Box
          component="video"
          // Keyed by URL so a re-signed link remounts the element rather than being assigned
          // to one already holding a failed source, which some browsers will not retry.
          key={videoUrl}
          ref={videoRef}
          src={videoUrl}
          controls
          /*
            The session's own first frame, which the run list already signs and which nothing
            was using. It costs no extra request and it is the recording -- a grey box would
            hold the space equally well and tell the technician nothing about which browser
            this is, which is exactly what they are scanning the transcript for.
          */
          poster={posterUrl || undefined}
          // The whole file, so the common case makes no further request and cannot be caught
          // by an expiring link at all.
          preload="auto"
          onError={onError}
          onLoadedMetadata={onLoadedMetadata}
          onPlaying={onPlaying}
          sx={{ display: "block", width: "100%", height: "100%" }}
        />
      ) : null}

      {/*
        One spinner for both waits, because to the technician they are one wait: the link being
        fetched, and the file being pulled once it has been. Over the poster rather than instead
        of it, so a recording that has a first frame shows it immediately and merely says it is
        still arriving.
      */}
      {!videoUrl || !loaded ? (
        <Box
          data-testid="computer-use-recording-loading"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            background: posterUrl ? "rgba(11, 18, 32, 0.35)" : "transparent",
          }}
        >
          <CircularProgress size={20} sx={{ color: "#C9D2E4" }} />
        </Box>
      ) : null}

      {label ? (
        <Typography
          sx={{
            position: "absolute",
            top: 6,
            left: 8,
            fontSize: 11,
            fontWeight: 600,
            color: "#E5EAF4",
            fontFamily: "Inter, sans-serif",
            textShadow: "0 1px 2px rgba(0,0,0,0.6)",
            pointerEvents: "none",
          }}
        >
          {label}
        </Typography>
      ) : null}

      {error ? (
        <Typography
          sx={{
            position: "absolute",
            bottom: 6,
            left: 8,
            fontSize: 11,
            color: "#FCA5A5",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ComputerUseRun;
